import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { SubscriptionPlanService } from '@/services';
import { QueryParams } from '@/model';
import { CreateSubscriptionPlanPlayload } from '../request/subscription-plan';

export const useSubscriptionPlans = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_SUBSCRIPTION_PLANS', params],
    async () => {
      const [err, data] = await SubscriptionPlanService.getSubscriptionPlans(params);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useSubscriptionPlansById = (id: string, params?: QueryParams, enabled?: boolean) => {
  const query = useQuery(
    ['GET_SUBSCRIPTION_PLANS', id, params],
    async () => {
      const [err, data] = await SubscriptionPlanService.getSubscriptionPlanById(id, params);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useCreateSubscriptionPlanMutation = () => {
  return useMutation({
    mutationKey: ['CREATE_SUBSCRIPTION_PLAN'],
    mutationFn: async (payload: CreateSubscriptionPlanPlayload) => {
      const [err, data] = await SubscriptionPlanService.createSubscriptionPlan(payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

type UseUpdateSubscriptionPlanParams = {
  id: string;
  payload: CreateSubscriptionPlanPlayload;
};
export const useUpdateSubscriptionPlanMutation = () => {
  return useMutation({
    mutationKey: ['UPDATE_SUBSCRIPTION_PLAN'],
    mutationFn: async ({ id, payload }: UseUpdateSubscriptionPlanParams) => {
      const [err, data] = await SubscriptionPlanService.updateSubscriptionPlan(id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};
